"use client";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  type TooltipContentProps,
} from "recharts";
import type { FullMonthlyProfile } from "@/lib/conditions/monthly-insights";
import { scoreOutOf100, scoreOutOf100Label } from "@/lib/conditions/score-display";

type Props = {
  months: FullMonthlyProfile[];
  bestMonthNumbers: number[];
};

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function scoreColor(score: number): string {
  if (score >= 8) return "#22C55E";
  if (score >= 6) return "#84CC16";
  if (score >= 4) return "#F59E0B";
  if (score >= 2) return "#F97316";
  return "#EF4444";
}

function renderTooltip(props: TooltipContentProps) {
  const { active, payload } = props;
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0].payload as FullMonthlyProfile & { label: string };

  return (
    <div className="bg-white rounded-lg border border-border-light shadow-lg p-3 text-[12px] min-w-[140px]">
      <div className="font-semibold text-navy mb-1">{point.label}</div>
      <div className="space-y-0.5 text-text-muted">
        <div>
          <span className="text-navy font-medium">Average:</span>{" "}
          {point.avgScore === null ? "Unavailable" : scoreOutOf100Label(point.avgScore)}
        </div>
        {point.bestWeeklyScore != null && (
          <div>
            <span className="text-navy font-medium">Best week:</span>{" "}
            {scoreOutOf100(point.bestWeeklyScore)}
          </div>
        )}
        {point.worstWeeklyScore != null && (
          <div>
            <span className="text-navy font-medium">Worst week:</span>{" "}
            {scoreOutOf100(point.worstWeeklyScore)}
          </div>
        )}
        <div>
          <span className="text-navy font-medium">Sample:</span>{" "}
          {point.sampleWeeks} weeks
        </div>
      </div>
    </div>
  );
}

export function MonthlyScoreGraph({ months, bestMonthNumbers }: Props) {
  if (months.length === 0) return null;

  const data = months.map((m) => ({
    ...m,
    label: MONTH_LABELS[m.month - 1] ?? "",
    isBest: bestMonthNumbers.includes(m.month),
  }));

  return (
    <div className="bg-white rounded-[16px] border border-border-light p-5 md:p-7">
      <h3 className="text-[12px] font-semibold uppercase tracking-[1.2px] text-text-muted mb-4">
        Monthly score pattern
      </h3>
      <div className="h-[200px] md:h-[240px]">
        <ResponsiveContainer
          width="100%"
          height="100%"
          initialDimension={{ width: 720, height: 200 }}
        >
          <LineChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: "#6b7280" }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              domain={[0, 10]}
              ticks={[0, 2, 4, 6, 8, 10]}
              tick={{ fontSize: 10, fill: "#6b7280" }}
              tickLine={false}
              axisLine={false}
              width={28}
            />
            <ReferenceLine y={6} stroke="#84CC16" strokeDasharray="3 3" strokeOpacity={0.4} />
            <ReferenceLine y={8} stroke="#22C55E" strokeDasharray="3 3" strokeOpacity={0.4} />
            <Tooltip content={renderTooltip} />
            <Line
              type="monotone"
              dataKey="avgScore"
              stroke="#1B2840"
              strokeWidth={2}
              dot={(dotProps) => {
                const { cx, cy, payload } = dotProps;
                if (payload.avgScore === null || cx == null || cy == null) {
                  return <g key={payload.month} />;
                }
                if (!payload.isBest) {
                  return (
                    <circle
                      key={payload.month}
                      cx={cx}
                      cy={cy}
                      r={3}
                      fill="#1B2840"
                      stroke="white"
                      strokeWidth={1.5}
                    />
                  );
                }
                return (
                  <circle
                    key={payload.month}
                    cx={cx}
                    cy={cy}
                    r={5}
                    fill={scoreColor(payload.avgScore)}
                    stroke="white"
                    strokeWidth={2}
                  />
                );
              }}
              activeDot={{ r: 5, fill: "#1B2840", stroke: "white", strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center gap-4 mt-3 text-[11px] text-text-muted flex-wrap">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500" />
          Best months
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-6 h-[1px] border-t border-dashed border-emerald-500" />
          Firing (8+)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-6 h-[1px] border-t border-dashed border-lime-500" />
          Great (6+)
        </span>
      </div>
    </div>
  );
}
